import React, { useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { useGetVehicleTypesQuery } from "../vehicleApiSlice";

interface VehicleTypesDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  error?: string;
  includeAll?: boolean;
  allLabel?: string;
  showSearch?: boolean;
  containerClassName?: string;
}

interface VehicleTypeOption {
  value: string;
  label: string;
}

const VehicleTypesDropdown: React.FC<VehicleTypesDropdownProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Vehicle type",
  name = "vehicle-type",
  id = "vehicle-type",
  showLabel = true,
  error,
  includeAll = false,
  allLabel = "All",
  showSearch = false,
  containerClassName,
}) => {
  const {
    data,
    isLoading,
    error: errorData,
    isError,
  } = useGetVehicleTypesQuery();

  const vehicleTypes = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  const vehicleTypeOptions: VehicleTypeOption[] = React.useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading vehicle types" }];
    }

    const mapped = vehicleTypes.map((typeObj: string) => ({
      value: typeObj,
      label: typeObj,
    }));

    if (includeAll) {
      return [{ value: "all", label: allLabel }, ...mapped];
    }

    return mapped;
  }, [vehicleTypes, isLoading, isError, includeAll, allLabel]);

  const handleValueChange = (option: VehicleTypeOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: VehicleTypeOption) => option.value;
  const getName = (option: VehicleTypeOption) => option.label;

  return (
    <div className={cn("w-full", containerClassName)}>
      <MySelect<VehicleTypeOption>
        name={name}
        id={id}
        label={showLabel ? "Vehicle type" : ""}
        options={vehicleTypeOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        showSearch={showSearch}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading partners"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default VehicleTypesDropdown;
